import React, { useState } from 'react';

import styled from 'styled-components';

import { deleteGroup, duplicateGroup, updateGroup } from 'apis/GroupAPI';
import Button from 'components/lib/Button';
import Dropdown from 'components/lib/Dropdown';
import Tooltip from 'components/lib/Tooltip';
import { GroupType } from 'types/groups';

import DownCaretIcon from '../../../common/Icons/DownCaretIcon';
import ConfirmModal from '../../../common/Modal/ConfirmModal';
import { useRootContext } from '../../layout/RootContext';
import GroupsCreateEditModal from '../create/GroupsCreateEditModal';
import { useGroupsContext } from '../GroupsContext';

type Props = {
  item: GroupType;
};

const Wrapper = styled.div`
  visibility: hidden;
`;

const GroupsTableActions = ({ item }: Props) => {
  const { showMessage } = useRootContext();
  const { refetch } = useGroupsContext();

  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openDuplicateModal, setOpenDuplicateModal] = useState<boolean>(false);

  const handleUpdate = async (group: GroupType) => {
    await updateGroup(item.id as number, group)
      .then((result) => {
        showMessage('success', 'Group updated successfully');
        refetch();
        setOpenEditModal(false);
      })
      .catch((err) => {
        showMessage('error', 'Trouble updating group');
      });
  };

  const handleDelete = async () => {
    await deleteGroup(item.id as number)
      .then((result) => {
        showMessage('success', 'Group deleted successfully');
        refetch();
        setOpenDeleteModal(false);
      })
      .catch((err) => {
        showMessage('error', 'Trouble deleting group');
      });
  };

  const handleDuplicate = async () => {
    await duplicateGroup(item.id as number)
      .then((result) => {
        showMessage('success', 'Group duplicated successfully');
        refetch();
        setOpenDuplicateModal(false);
      })
      .catch((err) => {
        showMessage('error', 'Trouble duplicating group');
      });
  };

  return (
    <Wrapper className={'hover-visible'}>
      {openEditModal && (
        <GroupsCreateEditModal
          open={openEditModal}
          group={item}
          handleClose={() => setOpenEditModal(false)}
          onSubmit={handleUpdate}
          title="Edit Group"
        />
      )}
      {openDeleteModal && (
        <ConfirmModal
          title={'Delete Group'}
          description={
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span>This will remove the selected group.</span>
              <span>Users related to group will lose access to communities related to the group.</span>
            </div>
          }
          confirmLabel={'Delete'}
          open={openDeleteModal}
          onConfirm={handleDelete}
          onCancel={() => setOpenDeleteModal(false)}
        />
      )}
      {openDuplicateModal && (
        <ConfirmModal
          title={'Duplicate Group'}
          description={
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span>This will create a duplicate of the selected group.</span>
            </div>
          }
          confirmLabel={'Duplicate'}
          open={openDuplicateModal}
          onConfirm={handleDuplicate}
          onCancel={() => setOpenDuplicateModal(false)}
        />
      )}

      <Dropdown
        menu={{
          items: [
            {
              label: 'Edit',
              key: 'edit',
              onClick: () => setOpenEditModal(true),
            },
            {
              label: 'Duplicate',
              key: 'duplicate',
              onClick: () => setOpenDuplicateModal(true),
            },
            {
              label:
                item.company || item.holding ? (
                  <Tooltip title={'Cannot delete a primary group'} color={'#000'} arrow={false}>
                    <div style={{ width: '100%' }}>Delete</div>
                  </Tooltip>
                ) : (
                  'Delete'
                ),
              key: 'delete',
              onClick: () => setOpenDeleteModal(true),
              disabled: (Boolean(item.company) || Boolean(item.holding)) && Boolean(item.primary),
            },
          ].filter(Boolean),
        }}
      >
        <Button style={{ width: 100 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>Actions</span>
            <DownCaretIcon />
          </div>
        </Button>
      </Dropdown>
    </Wrapper>
  );
};

export default GroupsTableActions;
